import { useEffect, useState } from "react";

const STATUS_MESSAGES = [
  "INITIALIZING SENTINEL NODES...",
  "SYNCHRONIZING EDGE REGIONS...",
  "ESTABLISHING SECURE CHANNEL...",
  "LOADING FLEET CONFIGURATION...",
  "CALIBRATING MONITOR INTERVALS...",
  "READY.",
];

export function NiceLoader({ message }: { message?: string }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const msgTimer = setInterval(() => {
      setMsgIndex((i) => Math.min(i + 1, STATUS_MESSAGES.length - 1));
    }, 600);
    return () => clearInterval(msgTimer);
  }, []);

  useEffect(() => {
    const dotTimer = setInterval(() => {
      setDots((d) => (d + 1) % 4);
    }, 350);
    return () => clearInterval(dotTimer);
  }, []);

  const currentMessage = message || STATUS_MESSAGES[msgIndex];

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center font-mono select-none overflow-hidden">
      {/* Radar Scanner */}
      <div className="relative w-48 h-48 mb-8">
        {/* Outer ring */}
        <div className="absolute inset-0 border-4 border-black rounded-full" />
        {/* Middle ring */}
        <div className="absolute inset-6 border-2 border-black/30 rounded-full" />
        {/* Inner ring */}
        <div className="absolute inset-12 border-2 border-black/15 rounded-full" />
        {/* Center dot */}
        <div className="absolute inset-[46%] bg-black rounded-full" />
        {/* Cross hair horizontal */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-black/20 -translate-y-px" />
        {/* Cross hair vertical */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/20 -translate-x-px" />

        {/* Spinning radar sweep */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{ animation: "spin 2s linear infinite" }}
        >
          <div
            className="absolute top-1/2 left-1/2 w-1/2 h-0.5 origin-left"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(0,0,0,0.9))",
            }}
          />
        </div>

        {/* Blip nodes */}
        <BlipNode top="20%" left="65%" delay="0.2s" />
        <BlipNode top="60%" left="25%" delay="0.8s" />
        <BlipNode top="35%" left="45%" delay="1.4s" />
        <BlipNode top="72%" left="62%" delay="0.5s" />
      </div>

      {/* Brand label */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-2xl font-black uppercase tracking-tighter text-black">
          UPTIMEWING
        </span>
        <span className="border-2 border-black px-1.5 py-0.5 text-[8px] font-black uppercase bg-yellow-300 tracking-widest">
          SENTINEL
        </span>
      </div>

      {/* Status message */}
      <div className="border-2 border-black px-6 py-3 min-w-[300px] text-center">
        <p className="text-[10px] font-black uppercase tracking-widest text-black">
          {currentMessage}
          {msgIndex < STATUS_MESSAGES.length - 1 && (
            <span>{".".repeat(dots)}</span>
          )}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mt-4 w-[300px] border-2 border-black h-2 overflow-hidden">
        <div
          className="h-full bg-black origin-left"
          style={{
            animation: "progress 3s ease-in-out forwards",
          }}
        />
      </div>

      {/* Inline style for custom animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes progress {
          0% { width: 5%; }
          20% { width: 30%; }
          50% { width: 60%; }
          80% { width: 85%; }
          100% { width: 100%; }
        }
        @keyframes blip-pulse {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}

function BlipNode({
  top,
  left,
  delay,
}: {
  top: string;
  left: string;
  delay: string;
}) {
  return (
    <div
      className="absolute w-2 h-2 rounded-full bg-black border border-black"
      style={{
        top,
        left,
        transform: "translate(-50%, -50%)",
        animation: `blip-pulse 2s ${delay} ease-in-out infinite`,
      }}
    />
  );
}
